import { excludeFrom, notNull, sampleRandom } from '@shared/misc.utils';

interface Klanken {
  klinkersEnkel: string[];
  klinkersDubbel: string[];
  klinkersTwee: string[];
  klinkersDrie: string[];
  klinkersVier: string[];
  medeklinkers: string[];
  medeklinkersSpeciaal: string[];
  medeklinkersSjwa: string[];
}
export class WordToKlankConverter {
  public static isKlinker(klank: string): boolean {
    return this.#klinkers.includes(klank);
  }
  public static isMedeklinker(klank: string): boolean {
    return this.#medeklinkers.includes(klank);
  }
  public static breakupWord(word: string): string[] {
    const klankenArray = Object.values(this.#klanken).flat();
    klankenArray.sort((a, b) => b.length - a.length);
    // Regex pattern to match any klank in the word
    const pattern = new RegExp(`(${klankenArray.join('|')})`, 'g');
    // Split the word on klanken using the pattern, e.g. "fiets" => ["f", "ie", "t", "s"]
    const result = word.split(pattern).filter(Boolean);
    return result.filter((item) => item.trim() !== '');
  }
  public static getKlinkers(): string[] {
    return this.#klinkers;
  }
  public static getMedeklinkers(): string[] {
    return this.#medeklinkers;
  }

  static readonly #klanken: Klanken = {
    klinkersEnkel: ['a', 'e', 'i', 'o', 'u'],
    klinkersDubbel: ['aa', 'ee', 'oo', 'uu'],
    klinkersTwee: ['ei', 'ij', 'ie', 'au', 'ou', 'oe', 'eu', 'ui', 'uw'],
    klinkersDrie: ['aai', 'ooi', 'oei', 'auw', 'ouw'],
    klinkersVier: ['eeuw', 'ieuw'],
    medeklinkers: ['b', 'c', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm', 'n', 'p', 'q', 'r', 's', 't', 'v', 'w'],
    medeklinkersSpeciaal: ['c', 'x', 'y', 'z', 'ch', 'ng', 'nk'],
    medeklinkersSjwa: ['ig', 'lijk'],
  };
  static readonly #klinkers = this.#klanken.klinkersEnkel.concat(
    this.#klanken.klinkersDubbel,
    this.#klanken.klinkersTwee,
    this.#klanken.klinkersDrie,
    this.#klanken.klinkersVier,
  );
  static readonly #medeklinkers = this.#klanken.medeklinkers;

  //return all klanken in a single array
  static getAllKlanken(): string[] {
    return this.#klinkers.concat(this.#medeklinkers, this.#klanken.medeklinkersSpeciaal, this.#klanken.medeklinkersSjwa);
  }

  static getAllEasyKlanken(): string[] {
    return excludeFrom(
      [
        ...this.#klanken.klinkersEnkel,
        ...this.#klanken.klinkersDubbel,
        ...this.#klanken.klinkersTwee,
        ...this.#klanken.klinkersDrie,
        ...this.#klanken.klinkersVier,
        ...this.#klanken.medeklinkers,
        ...this.#klanken.medeklinkersSpeciaal,
        ...this.#klanken.medeklinkersSjwa,
      ],
      'ig',
      'lijk',
      'c',
      'x',
      'y',
    );
  }

  static getKlankVariantsFor(klank: string, max: number = Infinity, except: string[] = []): string[] {
    if (this.#klanken.klinkersEnkel.includes(klank)) {
      return sampleRandom(excludeFrom(this.#klanken.klinkersEnkel, ...[klank, ...except]), max);
    }

    if (this.#klanken.klinkersDubbel.includes(klank)) {
      return sampleRandom(excludeFrom(this.#klanken.klinkersDubbel, ...[klank, ...except]), max);
    }

    if (this.#klanken.klinkersTwee.includes(klank)) {
      return sampleRandom(
        excludeFrom(
          this.#klanken.klinkersTwee,
          ...[
            klank,
            klank === 'ei' ? 'ij' : undefined,
            klank === 'ij' ? 'ei' : undefined,
            klank === 'au' ? 'ou' : undefined,
            klank === 'ou' ? 'au' : undefined,
            ...except,
          ].filter(notNull),
        ),
        max,
      );
    }

    if (this.#klanken.klinkersDrie.includes(klank)) {
      return sampleRandom(
        excludeFrom(
          this.#klanken.klinkersDrie,
          ...[
            klank,
            ...except,
            klank === 'auw' ? 'ouw' : undefined, //
            klank === 'ouw' ? 'auw' : undefined,
          ].filter(notNull),
        ),
        max,
      );
    }

    if (this.#klanken.klinkersVier.includes(klank)) {
      return sampleRandom(excludeFrom(this.#klanken.klinkersVier, ...[klank, ...except]), max);
    }

    if (this.#klanken.medeklinkers.includes(klank)) {
      return sampleRandom(
        excludeFrom(
          this.#klanken.medeklinkers,
          ...[
            klank, //
            ...except,
            'q',
            'c',
            klank === 'd' ? 't' : undefined,
            klank === 't' ? 'd' : undefined,
          ],
        ).filter(notNull),
        max,
      );
    }

    if (this.#klanken.medeklinkersSpeciaal.includes(klank)) {
      return sampleRandom(excludeFrom(this.#klanken.medeklinkersSpeciaal, ...[klank, ...except, 'c', 'x', 'y']), max);
    }

    if (this.#klanken.medeklinkersSjwa.includes(klank)) {
      return sampleRandom(excludeFrom(this.#klanken.medeklinkersSjwa, ...[klank, ...except]), max);
    }

    return [];
  }
}
